import classnames from 'classnames';
import PropTypes from 'prop-types';
import * as React from 'react';

import CheckboxFilter from './CheckboxFilter';
import HierarchicalFilter from './HierarchicalFilter';
import { useAccessibleInteractivity } from '../../../../hooks';
import { FilterHeading } from '../../../ComponentLibrary/text-elements/filters';
import windowWidth from '../../../helpers/window_width';

const FilterGroupHeading = React.forwardRef(({ expanded, onClick, title }, ref) => {
  useAccessibleInteractivity(ref, onClick);

  return (
    <legend>
      <FilterHeading
        font="body"
        className={classnames('filterList', { closed: !expanded })}
        ref={ref}
        role="button"
        aria-expanded={expanded}
      >
        {title}
      </FilterHeading>
    </legend>
  );
});

export default class Filter extends React.Component {
  static propTypes = {
    uid: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    component: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.object).isRequired,
    selected: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]).isRequired,
    onChange: PropTypes.func.isRequired,
    onHierarchicalChange: PropTypes.func.isRequired,
    filterGroupCount: PropTypes.number.isRequired,
    filters: PropTypes.arrayOf(PropTypes.object).isRequired,
    hierarchical: PropTypes.object,
    items: PropTypes.arrayOf(PropTypes.object).isRequired,
    filterFunc: PropTypes.func.isRequired,
    customFilterFactories: PropTypes.object,
  };

  constructor(props) {
    super(props);
    this.state = {
      windowWidth: windowWidth(),
      expanded: true,
      mobileExpanded: true,
    };
    this.updateWindowWidthState = this.updateWindowWidthState.bind(this);
    this.groupHeadingRef = React.createRef();
  }

  updateWindowWidthState() {
    this.setState({ windowWidth: windowWidth() });
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateWindowWidthState);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateWindowWidthState);
  }

  showInCurrentHierarchy() {
    const {
      currentHierarchy,
      extraProps: { visibleIfHierarchyMatches },
    } = this.props;
    if (!visibleIfHierarchyMatches) return true;

    return !!visibleIfHierarchyMatches.find(exp => (currentHierarchy || '').match(RegExp(exp.replace(/^\/|\/$/g, ''))));
  }

  // this is pretty expensive, runs every filter so we can see how many results would exist after applying each filter
  get optionsWithCounts() {
    if (this.props.component === 'HierarchicalFilter') {
      // not implemented for hierarchical. no point in computing for all options, only some are eligible to be shown.
      return this.props.options;
    }
    return this.props.options.map(o => {
      const filtersCpy = [...this.props.filters];
      const i = this.props.filters.findIndex(f => f.uid === this.props.uid);
      filtersCpy[i] = { ...filtersCpy[i], selected: _.uniq([o.slug]) };
      const cnt = this.props.filterFunc({ ...this.props, filters: filtersCpy }).length;
      return { ...o, count: cnt };
    });
  }

  render() {
    const expanded = this.state.windowWidth < 1050 ? this.state.mobileExpanded : this.state.expanded;

    const { title, component, options, selected, onChange, onHierarchicalChange, filters } = this.props;

    const commonProps = {
      options,
      selected,
      onChange,
      title,
      filters,
      className: classnames({ hidden: !expanded }),
    };

    if (component !== 'HierarchicalFilter') {
      commonProps.options = this.optionsWithCounts;
      // Don't show categories if all filter options lead to 0 results.
      if (!commonProps.options.some(o => o.count)) {
        return null;
      }
    }

    // Don't render filter without any options
    if (options.length === 0) {
      return null;
    }

    // Some filters can be conditionally hidden, depending on hierarchy
    if (!this.showInCurrentHierarchy()) {
      return null;
    }

    const filterFactories = {
      [component]: commonProps => <CheckboxFilter {...commonProps} />,
      HierarchicalFilter: commonProps => (
        <HierarchicalFilter
          {...commonProps}
          onChange={onHierarchicalChange}
          baseHierarchy={this.props.baseHierarchy}
          rootHierarchy={this.props.rootHierarchy}
          extraProps={this.props.extraProps}
          hierarchical={this.props.hierarchical}
          items={this.props.items}
          filterFunc={this.props.filterFunc}
          groupHeadingRef={this.groupHeadingRef}
        />
      ),
      ...this.props.customFilterFactories,
    };
    const Component = filterFactories[component];

    return (
      <fieldset className="filterGroup">
        <FilterGroupHeading
          expanded={expanded}
          onClick={() =>
            this.setState(prevState =>
              prevState.windowWidth < 1050
                ? { mobileExpanded: !prevState.mobileExpanded }
                : { expanded: !prevState.expanded }
            )
          }
          ref={this.groupHeadingRef}
          title={title}
        />
        <Component {...commonProps} />
      </fieldset>
    );
  }
}
